import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Star,
  Edit,
  MoreVertical,
  Settings,
  Trash2,
  CheckCircle,
  AlertCircle,
  Clock,
  ExternalLink
} from 'lucide-react'
import Button from '../ui/Button'
import Card from '../ui/Card'

const ProjectCard = ({
  project,
  isDefault = false,
  onEdit,
  onDelete,
  onSetDefault,
  onTestConnection,
  className = ''
}) => {
  const { t } = useTranslation()
  const [showActions, setShowActions] = useState(false)

  const getStatusIcon = () => {
    switch (project.verified ? 'verified' : 'unverified') {
      case 'verified':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'unverified':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />
      default:
        return <Clock className="w-4 h-4 text-gray-400" />
    }
  }

  const getStatusText = () => {
    if (project.verified) {
      return project.last_verified
        ? t('tms.lastVerified', { time: new Date(project.last_verified).toLocaleString() })
        : t('tms.verified')
    }
    return t('tms.needsVerification')
  }

  const handleActionClick = (action) => {
    setShowActions(false)
    switch (action) {
      case 'edit':
        onEdit?.(project)
        break
      case 'delete':
        onDelete?.(project)
        break
      case 'setDefault':
        onSetDefault?.(project)
        break
      case 'test':
        onTestConnection?.(project)
        break
    }
  }

  return (
    <Card className={`relative transition-all hover:shadow-lg ${className}`}>
      {/* Default Project Badge */}
      {isDefault && (
        <div className="absolute -top-2 -left-2 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-medium flex items-center">
          <Star className="w-3 h-3 mr-1 fill-current" />
          {t('tms.default')}
        </div>
      )}

      <Card.Content className="p-4">
        <div className="flex items-start justify-between">
          {/* Project Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                {project.name}
              </h3>
              {!project.active && (
                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded">
                  {t('tms.inactive')}
                </span>
              )}
            </div>

            <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
              <div>
                <span className="font-medium">{t('tms.projectId')}:</span>
                <span className="ml-2 font-mono text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                  {project.project_id}
                </span>
              </div>

              {project.default_tags && project.default_tags.length > 0 && (
                <div>
                  <span className="font-medium">{t('tms.tags')}:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {project.default_tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-2">
                {getStatusIcon()}
                <span className="text-xs">
                  {getStatusText()}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="relative ml-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowActions(!showActions)}
              className="p-1"
            >
              <MoreVertical className="w-4 h-4" />
            </Button>

            {showActions && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10">
                <div className="py-1">
                  <button
                    onClick={() => handleActionClick('edit')}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    {t('tms.editProject')}
                  </button>

                  {!isDefault && (
                    <button
                      onClick={() => handleActionClick('setDefault')}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                    >
                      <Star className="w-4 h-4 mr-2" />
                      {t('tms.setAsDefault')}
                    </button>
                  )}

                  <button
                    onClick={() => handleActionClick('test')}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {t('tms.testConnection')}
                  </button>

                  <div className="border-t border-gray-200 dark:border-gray-600 my-1" />

                  <button
                    onClick={() => handleActionClick('delete')}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {t('tms.deleteProject')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleActionClick('edit')}
              className="text-xs"
            >
              <Settings className="w-3 h-3 mr-1" />
              {t('tms.configure')}
            </Button>
          </div>

          <div className="text-xs text-gray-500 dark:text-gray-400">
            {project.default_overwrite_translations && (
              <span className="mr-2">{t('tms.overwrites')}</span>
            )}
            {project.default_flag_for_review && (
              <span className="mr-2">{t('tms.flagsReview')}</span>
            )}
            {project.create_missing_locales && (
              <span>{t('tms.createsLocales')}</span>
            )}
          </div>
        </div>
      </Card.Content>

      {/* Click outside to close actions menu */}
      {showActions && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => setShowActions(false)}
        />
      )}
    </Card>
  )
}

export default ProjectCard